import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { UserSettingKey, UserSettingResponse, userSettingsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface UpdateUserSettingVariables {
  key: UserSettingKey;
  value: boolean | null;
}

/**
 * Changes one setting of the signed-in user. The cached value is switched over before the request
 * completes so a toggle reacts immediately, and rolled back with a message if the write fails.
 */
export function useUpdateUserSetting() {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: UpdateUserSettingVariables) =>
      userSettingsApi.updateUserSetting({ key, updateUserSettingRequest: { value } }, withLocalErrorHandling),
    onMutate: async ({ key, value }: UpdateUserSettingVariables) => {
      await queryClient.cancelQueries({ queryKey: [QueryKey.UserSettings] });

      const previousSettings = queryClient.getQueryData<UserSettingResponse[]>([QueryKey.UserSettings]);

      queryClient.setQueryData<UserSettingResponse[]>([QueryKey.UserSettings], (current) => {
        const settingsByKey = new Map((current ?? []).map((setting) => [setting.key, setting]));
        settingsByKey.set(key, { key, value });

        return [...settingsByKey.values()];
      });

      return { previousSettings };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData([QueryKey.UserSettings], context?.previousSettings);
      showError(t("userSettings.saveFailed"));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.UserSettings] });
    },
  });
}
